import { ChangeDetectionStrategy, Component, effect, HostListener, inject, OnInit, signal } from '@angular/core';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SavedViewsModule } from '@shared/modules/saved-views/saved-views.module';
import { TsButtonModule, TsInputComponent, TsSnackbarService } from 'tsui';
import { SavedView } from '@core/models';
import { Store } from '@ngxs/store';
import { SavedViewsActions } from '@state/saved-view/action';
import { take } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SavedViewsState } from '@state/saved-view/state';

@Component({
  selector: 'saved-view-edit-dialog',
  standalone: true,
  imports: [TsDialogComponent, ReactiveFormsModule, SavedViewsModule, TsInputComponent, TsButtonModule],
  templateUrl: './saved-view-edit-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedViewEditDialogComponent implements OnInit {
  private readonly data: SavedView = inject(DIALOG_DATA);
  private readonly store = inject(Store);
  private readonly snackbarService = inject(TsSnackbarService);

  readonly dialogRef = inject(DialogRef);
  loading = signal(false);
  loadingEffect = effect(() => {
    if (this.loading()) {
      this.viewNameForm.disable();
    } else {
      this.viewNameForm.enable();
    }
  });
  viewNameForm = new FormControl('', Validators.required);

  @HostListener('keydown.enter', ['$event']) handleEnter() {
    if (!this.disableSaveButton) {
      this.onEditViewSave();
    }
  }

  get viewName() {
    return this.data.name;
  }

  get disableSaveButton() {
    return this.viewNameForm.invalid || this.viewNameForm.disabled || this.viewNameForm.value === this.viewName;
  }

  get savedViews() {
    return this.store.selectSnapshot(SavedViewsState.getViews);
  }

  ngOnInit(): void {
    this.viewNameForm.setValue(this.viewName);
  }

  onEditViewSave() {
    if (this.savedViewNameAlreadyExists(this.viewNameForm.value)) {
      this.snackbarService.open('A View with that name already exists in the system', 'error');
      return;
    }

    this.loading.set(true);

    this.store
      .dispatch(new SavedViewsActions.UpdateView({ id: this.data.id, name: this.viewNameForm.value }))
      .pipe(
        take(1),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe(() => {
        this.dialogRef.close({
          ...this.data,
          name: this.viewNameForm.value,
        });
      });
  }

  private savedViewNameAlreadyExists(name: string) {
    return this.savedViews.some((view) => view?.name.toLowerCase() === name.toLowerCase());
  }
}
